import React, { useEffect, useRef } from 'react';

/**
 * Le nuage de points de l'agent : ce qui bouge pendant qu'il travaille.
 *
 * Une quarantaine de points posés sur une sphère (spirale de Fibonacci),
 * chacun à son propre rayon — un nuage, pas une boule lisse — qui tourne
 * lentement sur lui-même et respire. La profondeur se lit à la taille et à
 * l'opacité : un point proche est plus gros et plus net.
 *
 * - La couleur est celle du texte de l'élément (`color`) : le thème la donne,
 *   le composant n'en écrit aucune.
 * - Mouvement réduit demandé : une image fixe, rien ne tourne.
 * - Décoratif : `aria-hidden`. Ce que fait l'agent est dit par le libellé à
 *   côté, dans une zone `role="status"`.
 */

const N = 44;

const POINTS = (() => {
  const pts = [];
  const angle = Math.PI * (3 - Math.sqrt(5));
  // Tirage déterministe : le nuage a la même forme à chaque ouverture.
  let graine = 7;
  const alea = () => { graine = (graine * 9301 + 49297) % 233280; return graine / 233280; };
  for (let i = 0; i < N; i++) {
    const y = 1 - (i / (N - 1)) * 2;
    const r = Math.sqrt(1 - y * y);
    const t = angle * i;
    const k = 0.55 + alea() * 0.5;
    pts.push({ x: Math.cos(t) * r * k, y: y * k, z: Math.sin(t) * r * k, phase: alea() * Math.PI * 2 });
  }
  return pts;
})();

export default function NuageReflexion({ taille = 40, className }) {
  const ref = useRef(null);

  useEffect(() => {
    const toile = ref.current;
    if (!toile) return undefined;
    const ctx = toile.getContext('2d');
    if (!ctx) return undefined;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    toile.width = Math.round(taille * dpr);
    toile.height = Math.round(taille * dpr);
    const calme = !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    let couleur = getComputedStyle(toile).color;
    let image = 0;
    let raf = 0;
    const debut = performance.now();

    const dessiner = (maintenant) => {
      const t = calme ? 1.2 : (maintenant - debut) / 1000;
      // Le thème peut changer pendant que l'agent travaille.
      if (++image % 45 === 0) couleur = getComputedStyle(toile).color;
      const ay = t * 0.85;
      const ax = 0.42 + Math.sin(t * 0.33) * 0.22;
      const R = taille * 0.44 * (1 + Math.sin(t * 2.1) * 0.05);
      const cy = Math.cos(ay), sy = Math.sin(ay), cx = Math.cos(ax), sx = Math.sin(ax);

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, taille, taille);
      ctx.fillStyle = couleur;
      for (const p of POINTS) {
        // Chaque point respire à son propre rythme : le nuage reste vivant.
        const j = 1 + Math.sin(t * 1.6 + p.phase) * 0.07;
        const x = p.x * cy - p.z * sy;
        const z0 = p.x * sy + p.z * cy;
        const y = p.y * cx - z0 * sx;
        const z = p.y * sx + z0 * cx;
        const proche = (z + 1) / 2;
        ctx.globalAlpha = 0.16 + proche * 0.84;
        ctx.beginPath();
        ctx.arc(taille / 2 + x * R * j, taille / 2 + y * R * j, 0.4 + proche * 0.85, 0, Math.PI * 2);
        ctx.fill();
      }
      if (!calme) raf = requestAnimationFrame(dessiner);
    };

    raf = requestAnimationFrame(dessiner);
    return () => cancelAnimationFrame(raf);
  }, [taille]);

  return <canvas ref={ref} className={className} width={taille} height={taille} style={{ width: taille, height: taille }} aria-hidden="true" />;
}
